import { useEffect } from 'react';
import './MoonblastAnimation.css';

/**
 * PROFESSIONAL MOONBLAST ANIMATION
 * Fairy-type special move with lunar energy blast effects
 * 
 * Key Features:
 * - Fairy-type special blast attack (95 base power)
 * - 30% chance Special Attack reduction visual effects
 * - Lunar energy blast with moon and fairy power
 * - Professional gaming company quality with celestial timing
 * - Special move animation with projectile mechanics
 */
export default function MoonblastAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate trajectory for projectile
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;

    // Elements to clean up
    const elements = [];

    // PHASE 1: Lunar Energy Buildup (0-500ms)
    console.log('Moonblast Phase 1: Lunar Energy Buildup');
    
    // Player enhancement with lunar aura
    playerEl.classList.add('moonblast-user-charge');
    
    // Lunar moon aura around player
    const moonAura = document.createElement('div');
    moonAura.className = 'moonblast-moon-aura';
    moonAura.style.left = `${playerX - 80}px`;
    moonAura.style.top = `${playerY - 80}px`;
    container.appendChild(moonAura);
    elements.push(moonAura);

    // Fairy energy particles around player
    for (let i = 0; i < 16; i++) {
      const particle = document.createElement('div');
      particle.className = 'moonblast-fairy-particle';
      const angle = (i / 16) * 360;
      const radius = 60 + (i % 3) * 15;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      particle.style.left = `${particleX - 5}px`;
      particle.style.top = `${particleY - 5}px`;
      particle.style.animationDelay = `${i * 30}ms`;
      particle.style.setProperty('--orbit-radius', `${radius}px`);
      container.appendChild(particle);
      elements.push(particle);
    }

    // Crescent moon symbols around player
    for (let i = 0; i < 6; i++) {
      const crescent = document.createElement('div');
      crescent.className = 'moonblast-crescent-moon';
      const crescentAngle = (i / 6) * 360;
      const crescentRadius = 70;
      const crescentX = playerX + Math.cos(crescentAngle * Math.PI / 180) * crescentRadius;
      const crescentY = playerY + Math.sin(crescentAngle * Math.PI / 180) * crescentRadius;
      crescent.style.left = `${crescentX - 12}px`;
      crescent.style.top = `${crescentY - 12}px`;
      crescent.style.animationDelay = `${i * 80}ms`;
      crescent.style.transform = `rotate(${crescentAngle}deg)`;
      container.appendChild(crescent);
      elements.push(crescent);
    }

    // PHASE 2: Moon Energy Projectile (500-1000ms)
    const phase2Timer = setTimeout(() => {
      console.log('Moonblast Phase 2: Moon Energy Projectile');
      
      // Create lunar energy orb at player center
      const moonOrb = document.createElement('div');
      moonOrb.className = 'moonblast-moon-orb';
      moonOrb.style.left = `${playerX}px`;
      moonOrb.style.top = `${playerY}px`;
      moonOrb.style.setProperty('--dx', `${dx}px`);
      moonOrb.style.setProperty('--dy', `${dy}px`);
      moonOrb.style.transform = 'translate(-50%, -50%)';
      container.appendChild(moonOrb);
      elements.push(moonOrb);

      // Lunar energy trail following orb
      const moonTrail = document.createElement('div');
      moonTrail.className = 'moonblast-moon-trail';
      moonTrail.style.left = `${playerX - 8}px`;
      moonTrail.style.top = `${playerY - 8}px`;
      moonTrail.style.width = `${distance}px`;
      moonTrail.style.transform = `rotate(${angle}deg)`;
      moonTrail.style.transformOrigin = 'left center';
      container.appendChild(moonTrail);
      elements.push(moonTrail);

      // Stardust particles following the orb
      for (let i = 0; i < 12; i++) {
        const stardustTimer = setTimeout(() => {
          const stardust = document.createElement('div');
          stardust.className = 'moonblast-stardust-particle';
          const starX = playerX + (dx * (i / 12)) + (Math.random() - 0.5) * 40;
          const starY = playerY + (dy * (i / 12)) + (Math.random() - 0.5) * 40;
          stardust.style.left = `${starX - 3}px`;
          stardust.style.top = `${starY - 3}px`;
          stardust.style.animationDelay = `${i * 20}ms`;
          container.appendChild(stardust);
          elements.push(stardust);
        }, i * 40);
        elements.push({ timer: stardustTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Lunar Explosion Impact (1000-1600ms)
    const phase3Timer = setTimeout(() => {
      console.log('Moonblast Phase 3: Lunar Explosion Impact');
      
      // Opponent hit reaction
      opponentEl.classList.add('moonblast-opponent-hit');

      // Massive lunar explosion at opponent
      const lunarExplosion = document.createElement('div');
      lunarExplosion.className = 'moonblast-lunar-explosion';
      lunarExplosion.style.left = `${opponentX - 120}px`;
      lunarExplosion.style.top = `${opponentY - 120}px`;
      container.appendChild(lunarExplosion);
      elements.push(lunarExplosion);

      // Moon symbol flash at impact
      const moonSymbol = document.createElement('div');
      moonSymbol.className = 'moonblast-moon-symbol';
      moonSymbol.textContent = '🌙';
      moonSymbol.style.left = `${opponentX - 25}px`;
      moonSymbol.style.top = `${opponentY - 25}px`;
      container.appendChild(moonSymbol);
      elements.push(moonSymbol);

      // Fairy energy burst rings
      for (let i = 0; i < 4; i++) {
        const ringTimer = setTimeout(() => {
          const energyRing = document.createElement('div');
          energyRing.className = 'moonblast-energy-ring';
          energyRing.style.left = `${opponentX - 80}px`;
          energyRing.style.top = `${opponentY - 80}px`;
          energyRing.style.animationDelay = `${i * 100}ms`;
          container.appendChild(energyRing);
          elements.push(energyRing);
        }, i * 120);
        elements.push({ timer: ringTimer, type: 'timer' });
      }

      // Lunar impact particles
      for (let i = 0; i < 20; i++) {
        const impactParticle = document.createElement('div');
        impactParticle.className = 'moonblast-impact-particle';
        const particleAngle = (i / 20) * 360;
        const particleRadius = 80 + Math.random() * 50;
        const particleX = opponentX + Math.cos(particleAngle * Math.PI / 180) * particleRadius;
        const particleY = opponentY + Math.sin(particleAngle * Math.PI / 180) * particleRadius;
        impactParticle.style.left = `${particleX - 4}px`;
        impactParticle.style.top = `${particleY - 4}px`;
        impactParticle.style.animationDelay = `${i * 25}ms`;
        impactParticle.style.setProperty('--scatter-x', `${Math.cos(particleAngle * Math.PI / 180) * 40}px`);
        impactParticle.style.setProperty('--scatter-y', `${Math.sin(particleAngle * Math.PI / 180) * 40}px`);
        container.appendChild(impactParticle);
        elements.push(impactParticle);
      }

      // Moonlight shimmer effect
      const moonlightShimmer = document.createElement('div');
      moonlightShimmer.className = 'moonblast-moonlight-shimmer';
      moonlightShimmer.style.left = `${opponentX - 100}px`;
      moonlightShimmer.style.top = `${opponentY - 100}px`;
      container.appendChild(moonlightShimmer);
      elements.push(moonlightShimmer);
    }, 1000);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Special Attack Reduction Effect (1600-2000ms)
    const phase4Timer = setTimeout(() => {
      console.log('Moonblast Phase 4: Special Attack Reduction Effect');
      
      // Remove charge effect
      playerEl.classList.remove('moonblast-user-charge');
      
      // Special Attack reduction visual effects only (no text)
      const debuffAura = document.createElement('div');
      debuffAura.className = 'moonblast-debuff-aura';
      debuffAura.style.left = `${opponentX - 70}px`;
      debuffAura.style.top = `${opponentY - 70}px`;
      container.appendChild(debuffAura);
      elements.push(debuffAura);

      // Weakening particles around opponent
      for (let i = 0; i < 10; i++) {
        const weakenParticle = document.createElement('div');
        weakenParticle.className = 'moonblast-weaken-particle';
        const angle = (i / 10) * 360;
        const radius = 55;
        const particleX = opponentX + Math.cos(angle * Math.PI / 180) * radius;
        const particleY = opponentY + Math.sin(angle * Math.PI / 180) * radius;
        weakenParticle.style.left = `${particleX - 3}px`;
        weakenParticle.style.top = `${particleY - 3}px`;
        weakenParticle.style.animationDelay = `${i * 40}ms`;
        container.appendChild(weakenParticle);
        elements.push(weakenParticle);
      }
    }, 1600);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (2000ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Moonblast: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('moonblast-user-charge');
      opponentEl.classList.remove('moonblast-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 2000);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('moonblast-user-charge');
      opponentEl.classList.remove('moonblast-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
MoonblastAnimation.moveType = 'fairy';
